import { EVENT_LABELS, type EntryFull, type EntryStatus, type EventType } from '@siteday/shared';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Check, Lock, Wrench } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { entriesApi, errorMessage, jobsApi, qk, reportsApi } from '@/api';
import {
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
} from '@/components/PageHeader';
import { Pill } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { Card, CardBody, CardHeader, Table } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { useCompanyBasics } from '@/features/auth/session';
import { useEntriesList } from '@/features/entries/useEntries';
import { useJobsList } from '@/features/jobs/useJobs';
import { useQboStatus } from '@/features/jobs/JobForm';
import { cn } from '@/lib/cn';
import { downloadText } from '@/lib/csv';
import { addDaysOnly, fmtWeekRange, todayInTz, weekStartFor } from '@/lib/dates';
import { formatMinutes, formatQuantity, hrs } from '@/lib/format';
import { format, parseISO } from 'date-fns';

import { buildRollup, rollupCsv, type MatrixRow } from './rollup';

const DEFAULTS = { job: '', week: '' };
const WEEK_START_DAY = 'mon';

/** W9 — job + week, quantities × day matrix, labor by person, equipment by machine, day status, PDF/CSV, approve week, export. */
export function RollupPage() {
  const { tz } = useCompanyBasics();
  const company = useQuery({
    queryKey: qk.company(),
    queryFn: () => import('@/api/company').then((m) => m.getCompany()),
    staleTime: 5 * 60_000,
  });
  const weekStartDay = company.data?.weekStartDay ?? WEEK_START_DAY;
  const today = todayInTz(tz);
  const [state, setState] = useUrlStateLocal(DEFAULTS);
  const jobs = useJobsList({ status: 'active' });
  const jobId = state.job || jobs.data?.items[0]?.id || '';
  const weekStart = state.week || weekStartFor(today, weekStartDay);
  const weekEnd = addDaysOnly(weekStart, 6);
  const job = jobs.data?.items.find((j) => j.id === jobId);
  const qbo = useQboStatus();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const list = useEntriesList({ jobId, from: weekStart, to: weekEnd }, !!jobId);
  const codes = useQuery({
    queryKey: qk.costCodes.list(jobId),
    queryFn: () => jobsApi.listCostCodes(jobId),
    enabled: !!jobId,
  });
  const fulls = useQueries({
    queries: (list.data?.items ?? []).map((e) => ({
      queryKey: qk.entries.detail(e.id),
      queryFn: () => entriesApi.getEntry(e.id),
    })),
  });
  const loading = list.isPending || codes.isPending || fulls.some((q) => q.isPending);
  const entries = useMemo(
    () => fulls.map((q) => q.data).filter((e): e is EntryFull => !!e),
    [fulls],
  );
  const rollup = useMemo(
    () => (codes.data ? buildRollup(weekStart, entries, codes.data.items) : null),
    [weekStart, entries, codes.data],
  );

  const approve = useMutation({
    mutationFn: () => entriesApi.approveWeek(jobId, weekStart),
    onSuccess: (r) => {
      toast.success(`${r.approved} entries approved`);
      void qc.invalidateQueries({ queryKey: qk.entries.all() });
      void qc.invalidateQueries({ queryKey: qk.dashboard.all() });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  useEffect(() => {
    if (!state.job && jobs.data?.items[0]) setState({ job: jobs.data.items[0].id });
  }, [state.job, jobs.data, setState]);

  const dayHead = (d: string) => format(parseISO(d), 'EEE d');

  return (
    <>
      <PageHeader
        title="Weekly roll-up"
        actions={
          <>
            <a
              href={reportsApi.weeklyReportUrl(jobId, weekStart, 'pdf')}
              target="_blank"
              rel="noreferrer"
            >
              <Button disabled={!jobId}>Download PDF</Button>
            </a>
            <Button
              disabled={!rollup || !job}
              onClick={() =>
                rollup &&
                job &&
                downloadText(
                  `${job.name.replace(/[^\w]+/g, '-')}-week-${weekStart}.csv`,
                  rollupCsv(job.name, weekStart, rollup.entries),
                )
              }
            >
              Download CSV
            </Button>
            <Button
              variant="dark"
              disabled={!rollup || rollup.finishedCount === 0}
              loading={approve.isPending}
              onClick={() => approve.mutate()}
            >
              Approve week{rollup ? ` (${rollup.finishedCount} finished)` : ''}
            </Button>
            <Button
              variant="primary"
              disabled={!jobId}
              onClick={() => navigate(`/quickbooks?tab=export&job=${jobId}&week=${weekStart}`)}
            >
              Export to QuickBooks
            </Button>
          </>
        }
      />
      <FilterBar>
        <Select
          aria-label="Job"
          value={jobId}
          onChange={(e) => setState({ job: e.target.value })}
          className="w-auto max-w-72"
        >
          {jobs.data?.items.map((j) => (
            <option key={j.id} value={j.id}>
              Job · {j.name}
            </option>
          ))}
        </Select>
        <Input
          type="date"
          aria-label="Week"
          value={weekStart}
          onChange={(e) =>
            e.target.value && setState({ week: weekStartFor(e.target.value, weekStartDay) })
          }
          className="w-auto"
        />
        <span className="font-medium">{fmtWeekRange(weekStart)}</span>
        <span className="text-ink3">
          Week starts {weekStartDay === 'mon' ? 'Monday' : weekStartDay} (Settings)
        </span>
        {!qbo.data?.connected ? (
          <span className="ml-auto text-xs text-ink3">
            QuickBooks not connected — export previews only
          </span>
        ) : null}
      </FilterBar>

      {list.isError ? <ErrorState error={list.error} onRetry={() => void list.refetch()} /> : null}
      {loading && jobId ? <LoadingState /> : null}
      {rollup && !loading ? (
        <>
          <Card className="mb-4">
            <CardHeader title="Quantities" />
            <Table className="matrix">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Description</th>
                  {rollup.days.map((d) => (
                    <th key={d} className="r">
                      {dayHead(d)}
                    </th>
                  ))}
                  <th className="r">Week</th>
                  <th className="r">Plan / wk</th>
                </tr>
              </thead>
              <tbody>
                {rollup.quantities.length === 0 ? (
                  <tr>
                    <td colSpan={11}>
                      <EmptyState>No cost codes on this job.</EmptyState>
                    </td>
                  </tr>
                ) : null}
                {rollup.quantities.map((r) => (
                  <MatrixTr key={r.key} row={r} fmt={formatQuantity} />
                ))}
                <tr className="tot">
                  <td colSpan={2}>Labor hours</td>
                  {rollup.laborByDay.map((v, i) => (
                    <td key={i} className={cn('r', v === null && 'z')}>
                      {v === null ? '—' : hrs(v)}
                    </td>
                  ))}
                  <td className="r">{hrs(rollup.laborTotal)}</td>
                  <td />
                </tr>
                <tr className="tot">
                  <td colSpan={2}>Machine hours</td>
                  {rollup.machineByDay.map((v, i) => (
                    <td key={i} className={cn('r', v === null && 'z')}>
                      {v === null ? '—' : hrs(v)}
                    </td>
                  ))}
                  <td className="r">{hrs(rollup.machineTotal)}</td>
                  <td />
                </tr>
                <tr>
                  <td colSpan={2} className="text-ink3">
                    Day status
                  </td>
                  {rollup.dayStatus.map((s, i) => (
                    <td key={i} className="r">
                      <DayStatus status={s} />
                    </td>
                  ))}
                  <td />
                  <td />
                </tr>
              </tbody>
            </Table>
          </Card>
          <div className="two">
            <Card>
              <CardHeader title="Labor by person" />
              <Table className="matrix">
                <thead>
                  <tr>
                    <th>Name</th>
                    {rollup.days.map((d, i) =>
                      rollup.laborByDay[i] !== null ? (
                        <th key={d} className="r">
                          {format(parseISO(d), 'EEE')}
                        </th>
                      ) : null,
                    )}
                    <th className="r">Week</th>
                  </tr>
                </thead>
                <tbody>
                  {rollup.labor.length === 0 ? (
                    <tr>
                      <td colSpan={9}>
                        <EmptyState>No labor this week.</EmptyState>
                      </td>
                    </tr>
                  ) : null}
                  {rollup.labor.map((r) => (
                    <MatrixTr
                      key={r.key}
                      row={r}
                      fmt={(v) => hrs(v)}
                      hideDays={rollup.laborByDay.map((v) => v === null)}
                      noSub
                    />
                  ))}
                </tbody>
              </Table>
            </Card>
            <Card>
              <CardHeader title="Equipment by machine" />
              <Table className="matrix">
                <thead>
                  <tr>
                    <th>Machine</th>
                    {rollup.days.map((d, i) =>
                      rollup.laborByDay[i] !== null ? (
                        <th key={d} className="r">
                          {format(parseISO(d), 'EEE')}
                        </th>
                      ) : null,
                    )}
                    <th className="r">Week</th>
                  </tr>
                </thead>
                <tbody>
                  {rollup.equipment.length === 0 ? (
                    <tr>
                      <td colSpan={9}>
                        <EmptyState>No machines this week.</EmptyState>
                      </td>
                    </tr>
                  ) : null}
                  {rollup.equipment.map((r) => (
                    <MatrixTr
                      key={r.key}
                      row={r}
                      fmt={(v) => hrs(v)}
                      hideDays={rollup.laborByDay.map((v) => v === null)}
                      noSub
                    />
                  ))}
                </tbody>
              </Table>
              <CardHeader title="Delays this week" className="border-t border-line2" />
              <CardBody className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-2">
                {rollup.events.length === 0 ? (
                  <span className="text-ink3">No delays logged</span>
                ) : null}
                {rollup.events.map((ev) => (
                  <span key={ev.type} className="inline-flex items-center gap-1.5">
                    <Wrench className="size-3.5 text-ink3" />{' '}
                    {EVENT_LABELS[ev.type as EventType] ?? ev.type} · {ev.count} event
                    {ev.count === 1 ? '' : 's'} · {formatMinutes(ev.minutes)}
                  </span>
                ))}
                <span className="inline-flex items-center gap-1.5">
                  <Camera className="size-3.5 text-ink3" /> {rollup.photoCount} photos
                </span>
              </CardBody>
            </Card>
          </div>
        </>
      ) : null}
    </>
  );
}

function MatrixTr({
  row,
  fmt,
  hideDays,
  noSub,
}: {
  row: MatrixRow;
  fmt: (v: string) => string;
  hideDays?: boolean[] | undefined;
  noSub?: boolean | undefined;
}) {
  return (
    <tr>
      <td>{noSub ? row.label : <code>{row.label}</code>}</td>
      {noSub ? null : <td>{row.sub}</td>}
      {row.byDay.map((v, i) =>
        hideDays?.[i] ? null : (
          <td key={i} className={cn('r', v === null && 'z')}>
            {v === null ? '—' : fmt(v)}
          </td>
        ),
      )}
      <td className="r font-bold">{fmt(row.total)}</td>
      {row.planPerWeek !== undefined ? (
        <td className="r text-ink3">
          {row.planPerWeek === null ? '—' : formatQuantity(row.planPerWeek)}
        </td>
      ) : null}
    </tr>
  );
}

function DayStatus({ status }: { status: EntryStatus | 'none' }) {
  if (status === 'none') return <Pill>none</Pill>;
  if (status === 'approved')
    return (
      <Pill tone="info">
        <Lock /> Approved
      </Pill>
    );
  if (status === 'needs_review') return <Pill tone="bad">Review</Pill>;
  if (status === 'draft') return <Pill tone="warn">Draft</Pill>;
  return (
    <Pill tone="good">
      <Check /> Finished
    </Pill>
  );
}

// Local alias so the page reads as one unit; the shared hook keeps filters in the URL.
import { useUrlState as useUrlStateLocal } from '@/lib/url-state';
